import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService, auth } from '../services/dataService';
import { excelService } from '../services/excelService';
import { Car, CarStatus, CarRequest, TestDriveRequest } from '../types';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  Database, FileSpreadsheet, ShieldCheck, Flame, Server, Sparkles, Check, 
  AlertTriangle, UploadCloud, Download, RefreshCw, Layers, Terminal, 
  ArrowLeft, ExternalLink, ChevronRight, CheckCircle2, AlertCircle, Info
} from 'lucide-react';

export const DatabaseSettings: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<'staff' | 'admin' | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Stats State
  const [cars, setCars] = useState<Car[]>([]);
  const [requests, setRequests] = useState<CarRequest[]>([]);
  const [testDrives, setTestDrives] = useState<TestDriveRequest[]>([]);
  const [isStatsLoading, setIsStatsLoading] = useState(false);

  // Sync Engine State
  const [syncMode, setSyncMode] = useState<'merge' | 'append' | 'replace'>('merge');
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelFileName, setExcelFileName] = useState<string>('');
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [excelImportCars, setExcelImportCars] = useState<Omit<Car, 'id'>[] | null>(null);
  const [excelImportErrors, setExcelImportErrors] = useState<string[] | null>(null);
  const [syncLogs, setSyncLogs] = useState<string[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
        if (user.email === 'nassoroa@gmail.com' || user.email?.includes('admin')) {
          setUserRole('admin');
        } else {
          setUserRole('staff');
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
        // Direct to admin login page if unauthorized
        navigate('/admin');
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  // Load database metadata/collections info
  const loadDatabaseStats = async () => {
    setIsStatsLoading(true);
    try {
      const [allCars, allRequests, allTestDrives] = await Promise.all([
        dataService.getCars(),
        dataService.getCarRequests(),
        dataService.getTestDrives()
      ]);
      setCars(allCars);
      setRequests(allRequests);
      setTestDrives(allTestDrives);
    } catch (err) {
      console.error("Failed to fetch collections count:", err);
    } finally {
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadDatabaseStats();
    }
  }, [isAuthenticated]);

  // Handle excel drag & drop or file choose
  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processSelectedFile(file);
    e.target.value = ''; // reset element
  };

  const processSelectedFile = async (file: File) => {
    setExcelFile(file);
    setExcelFileName(file.name);
    setIsProcessingExcel(true);
    setExcelImportErrors(null);
    setExcelImportCars(null);
    setSyncSuccess(false);
    setSyncLogs([`[INFO] File selected: "${file.name}"`, `[INFO] Parsing workbook spreadsheet...`]);

    try {
      const result = await excelService.parseExcelFile(file);
      if (result.success && result.data.length > 0) {
        setExcelImportCars(result.data);
        setSyncLogs(prev => [
          ...prev, 
          `[SUCCESS] Parsed ${result.data.length} vehicle rows successfully.`,
          `[INFO] Validation passed. Ready for synchronization.`
        ]);
      } else {
        const errors = result.errors.length > 0 ? result.errors : ['No valid vehicle data structure found in the spreadsheet.'];
        setExcelImportErrors(errors);
        setSyncLogs(prev => [
          ...prev, 
          `[ERROR] Verification failed. Found ${errors.length} structure discrepancies.`,
          ...errors.map(err => `[FAIL] ${err}`)
        ]);
      }
    } catch (err: any) {
      const errMsg = err.message || 'Error processing spreadsheet file';
      setExcelImportErrors([errMsg]);
      setSyncLogs(prev => [...prev, `[CRITICAL] ${errMsg}`]);
    } finally {
      setIsProcessingExcel(false);
    }
  };

  // Run the cloud sync engine
  const runCloudSyncEngine = async () => {
    if (!excelImportCars || excelImportCars.length === 0) return;
    
    // Safety prompt warnings based on mode
    if (syncMode === 'replace') {
      const confirmed = window.confirm(
        "CRITICAL OVERWRITE ACTION:\nThis will permanently DELETE all current vehicles on Dakar Auto Sales lot and replace them with the records in this Excel sheet. This is irreversible.\n\nAre you sure you want to proceed?"
      );
      if (!confirmed) return;
    } else if (syncMode === 'append') {
      const confirmed = window.confirm(
        `Are you sure you want to ADD all ${excelImportCars.length} vehicles directly to the catalog as new entries? (Could create duplicates if the same vehicles are already listed).`
      );
      if (!confirmed) return;
    } else {
      const confirmed = window.confirm(
        `Initialize Smart Inventory Update:\n- New VINs will be added to the lot.\n- Existing VINs will be fully updated to match the Excel sheet's prices, statuses, and details.\n- Other current listings will remain unchanged.\n\nProceed?`
      );
      if (!confirmed) return;
    }

    setIsSyncing(true);
    setSyncSuccess(false);
    setSyncLogs(prev => [...prev, `[START] Initializing Firestore Connection...`]);

    try {
      // 1. Get current list from DB
      const currentList = await dataService.getCars();
      setSyncLogs(prev => [...prev, `[SYNC] Fetched ${currentList.length} existing Firestore records.`]);

      let addedCount = 0;
      let updatedCount = 0;
      let deletedCount = 0;

      // 2. Execute Replace if needed
      if (syncMode === 'replace') {
        setSyncLogs(prev => [...prev, `[PURGE] Wiping existing inventory catalog...`]);
        for (const car of currentList) {
          await dataService.deleteCar(car.id);
          deletedCount++;
        }
        setSyncLogs(prev => [...prev, `[PURGE] Erased ${deletedCount} obsolete lot entries.`]);
        
        // Add all from Excel
        for (const excelCar of excelImportCars) {
          setSyncLogs(prev => [...prev, `[INSERT] Creating listing: ${excelCar.year} ${excelCar.make} ${excelCar.model}...`]);
          await dataService.addCar(excelCar);
          addedCount++;
        }
      } 
      
      // 3. Execute Append
      else if (syncMode === 'append') {
        for (const excelCar of excelImportCars) {
          setSyncLogs(prev => [...prev, `[INSERT] Appending listing: ${excelCar.year} ${excelCar.make} ${excelCar.model}...`]);
          await dataService.addCar(excelCar);
          addedCount++;
        }
      } 
      
      // 4. Execute Smart Merge (VIN Based)
      else {
        // Create lookup of existing cars by VIN (lowercase to normalize)
        const existingVinMap = new Map<string, Car>();
        currentList.forEach(c => {
          if (c.vin) {
            existingVinMap.set(c.vin.trim().toLowerCase(), c);
          }
        });

        for (const excelCar of excelImportCars) {
          const normalizedVin = excelCar.vin ? excelCar.vin.trim().toLowerCase() : '';
          const existingCar = normalizedVin ? existingVinMap.get(normalizedVin) : null;

          if (existingCar) {
            setSyncLogs(prev => [...prev, `[UPDATE] Matching VIN ${excelCar.vin} found. Synchronizing details...`]);
            await dataService.updateCar(existingCar.id, excelCar);
            updatedCount++;
          } else {
            setSyncLogs(prev => [...prev, `[INSERT] No VIN match for ${excelCar.vin}. Registering new entry...`]);
            await dataService.addCar(excelCar);
            addedCount++;
          }
        }
      }

      setSyncLogs(prev => [
        ...prev,
        `[SUCCESS] Cloud synchronization complete!`,
        `[SUMMARY] +${addedCount} Created | ~${updatedCount} Updated | -${deletedCount} Cleared`
      ]);
      setSyncSuccess(true);
      setExcelImportCars(null);
      setExcelFile(null);
      setExcelFileName('');
      loadDatabaseStats();
    } catch (err: any) {
      const errMsg = err.message || 'Cloud write operation failed';
      setSyncLogs(prev => [...prev, `[CRITICAL ERROR] Firestore Sync Failed: ${errMsg}`]);
      alert(`Sync failed: ${errMsg}`);
    } finally {
      setIsSyncing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-brandGray flex items-center justify-center">
        <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] text-brandDark p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Breadcrumb / Top Bar */}
        <div className="flex justify-between items-center mb-8 bg-white py-4 px-6 rounded-2xl border border-gray-100 shadow-sm">
          <button 
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400 hover:text-brandDark transition-colors"
          >
            <ArrowLeft size={16} /> Return to Workspace
          </button>
          
          <div className="flex items-center gap-3">
            <span className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse"></span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Connected to Firestore</span>
          </div>
        </div>

        {/* Two Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-20">
          
          {/* LEFT COLUMN: FIREBASE PROJECT SETTINGS MOCK (REPLICATING SCREENSHOT) */}
          <div className="lg:col-span-5 space-y-8">
            
            {/* High-Fidelity Firebase settings mock */}
            <div className="bg-white rounded-3xl border border-gray-200/80 shadow-lg overflow-hidden">
              <div className="bg-[#1a1c23] px-6 py-5 flex items-center justify-between border-b border-gray-800">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 bg-[#FFCA28] rounded-lg">
                    <Flame size={18} className="text-[#1a1c23] fill-[#1a1c23]" />
                  </div>
                  <span className="text-white font-heading text-sm font-bold uppercase tracking-wider">Project settings</span>
                </div>
                <div className="text-[9px] text-[#A0AEC0] font-mono tracking-widest uppercase">General</div>
              </div>

              <div className="p-8 space-y-6">
                <div>
                  <h3 className="text-brandDark font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2 mb-1">
                    <Server size={18} className="text-primary" />
                    Your project
                  </h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Active cloud infrastructure details</p>
                </div>

                {/* Settings Table mirroring screenshot fields */}
                <div className="divide-y divide-gray-100 border-t border-b border-gray-100 font-sans text-xs">
                  <div className="grid grid-cols-12 py-3.5 items-center">
                    <span className="col-span-5 text-gray-400 font-bold uppercase tracking-wider text-[10px]">Project name</span>
                    <span className="col-span-7 text-brandDark font-bold flex items-center gap-2">
                      Dakar1
                      <span className="px-1.5 py-0.5 rounded text-[8px] bg-primary/10 text-primary font-bold">Default</span>
                    </span>
                  </div>

                  <div className="grid grid-cols-12 py-3.5 items-center">
                    <span className="col-span-5 text-gray-400 font-bold uppercase tracking-wider text-[10px]">Project ID</span>
                    <span className="col-span-7 font-mono text-gray-700 font-semibold text-[11px]">dakar1</span>
                  </div>

                  <div className="grid grid-cols-12 py-3.5 items-center">
                    <span className="col-span-5 text-gray-400 font-bold uppercase tracking-wider text-[10px]">Project number</span>
                    <span className="col-span-7 font-mono text-gray-700 font-semibold text-[11px]">908496794258</span>
                  </div>

                  <div className="grid grid-cols-12 py-3.5 items-center">
                    <span className="col-span-5 text-gray-400 font-bold uppercase tracking-wider text-[10px]">Environment type</span>
                    <span className="col-span-7 text-brandDark font-bold flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      Production
                    </span>
                  </div>

                  <div className="grid grid-cols-12 py-3.5 items-center">
                    <span className="col-span-5 text-gray-400 font-bold uppercase tracking-wider text-[10px]">Cloud Provider</span>
                    <span className="col-span-7 text-brandDark font-bold text-[11px] flex items-center gap-1">
                      Google Cloud Platform
                      <ExternalLink size={11} className="text-gray-400" />
                    </span>
                  </div>
                </div>

                {/* Database Health Monitor */}
                <div className="bg-[#F9FAFB] p-5 rounded-2xl border border-gray-100/80">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-brandDark mb-3 flex items-center gap-1.5">
                    <RefreshCw size={12} className="text-primary animate-spin-slow" />
                    Live Database Health
                  </h4>
                  <div className="space-y-2 text-[11px]">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">API Status</span>
                      <span className="text-emerald-700 font-bold">200 OK</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">Ingress Host</span>
                      <span className="text-brandDark font-mono">Cloud Run (us-west2)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Document stats / Active Counts from Firestore */}
            <div className="bg-white rounded-3xl border border-gray-200/80 shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-brandDark font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2">
                    <Layers size={18} className="text-primary" />
                    Cloud Collections
                  </h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Real-time counts stored on dakar1</p>
                </div>
                <button 
                  onClick={loadDatabaseStats} 
                  disabled={isStatsLoading}
                  className="p-2 text-gray-400 hover:text-brandDark rounded-xl hover:bg-gray-50 transition-all border border-gray-100 shadow-sm"
                  title="Query database totals"
                >
                  <RefreshCw size={14} className={isStatsLoading ? "animate-spin text-primary" : ""} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-brandGray/30 p-4 rounded-2xl border border-gray-100/50">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest block mb-1">Cars</span>
                  {isStatsLoading ? (
                    <div className="h-6 w-12 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <span className="text-2xl font-bold text-brandDark font-heading">{cars.length}</span>
                  )}
                </div>

                <div className="bg-brandGray/30 p-4 rounded-2xl border border-gray-100/50">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest block mb-1">Test Drives</span>
                  {isStatsLoading ? (
                    <div className="h-6 w-12 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <span className="text-2xl font-bold text-primary font-heading">{testDrives.length}</span>
                  )}
                </div>

                <div className="bg-brandGray/30 p-4 rounded-2xl border border-gray-100/50">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest block mb-1">Requests</span>
                  {isStatsLoading ? (
                    <div className="h-6 w-12 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <span className="text-2xl font-bold text-accent font-heading">{requests.length}</span>
                  )}
                </div>

                <div className="bg-brandGray/30 p-4 rounded-2xl border border-gray-100/50">
                  <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest block mb-1">Connection</span>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-lg w-fit mt-1 block uppercase tracking-wide">Secure</span>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN: INTELLIGENT EXCEL SYNC ENGINE */}
          <div className="lg:col-span-7 space-y-8">
            
            <div className="bg-white rounded-3xl border border-gray-200/80 shadow-lg p-8 md:p-10">
              
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 border-b border-gray-100 pb-6">
                <div>
                  <h2 className="text-2xl font-heading font-bold uppercase tracking-wider text-brandDark flex items-center gap-2.5">
                    <FileSpreadsheet className="text-emerald-600" size={26} />
                    Excel Sync Board
                  </h2>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                    Keep your website car inventory matched with local Excel databases
                  </p>
                </div>

                <button 
                  onClick={() => excelService.downloadTemplate()}
                  className="bg-brandGray hover:bg-gray-200/70 border border-gray-200 text-gray-600 px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all shadow-sm"
                >
                  <Download size={13} /> Download Template
                </button>
              </div>

              {/* Sync Behavior Mode selection cards */}
              <div className="mb-8">
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-3">
                  Choose Synchronizing Strategy
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  
                  {/* Smart Merge */}
                  <div 
                    onClick={() => setSyncMode('merge')}
                    className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                      syncMode === 'merge' 
                        ? 'border-primary bg-primary/[0.02] shadow-sm' 
                        : 'border-gray-100 hover:border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={16} className={syncMode === 'merge' ? 'text-primary' : 'text-gray-400'} />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-brandDark">Smart Merge</span>
                    </div>
                    <p className="text-[10px] text-gray-400 leading-relaxed font-semibold">
                      Compares spreadsheet VINs. Updates pricing/mileage on matches; appends new items. Leaves existing others intact.
                    </p>
                  </div>

                  {/* Append Mode */}
                  <div 
                    onClick={() => setSyncMode('append')}
                    className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                      syncMode === 'append' 
                        ? 'border-primary bg-primary/[0.02] shadow-sm' 
                        : 'border-gray-100 hover:border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Layers size={16} className={syncMode === 'append' ? 'text-primary' : 'text-gray-400'} />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-brandDark">Add / Append</span>
                    </div>
                    <p className="text-[10px] text-gray-400 leading-relaxed font-semibold">
                      Appends all entries from the Excel sheet into Firestore as new listings without matching existing lot data.
                    </p>
                  </div>

                  {/* Overwrite Mode */}
                  <div 
                    onClick={() => setSyncMode('replace')}
                    className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                      syncMode === 'replace' 
                        ? 'border-danger bg-danger/[0.01] shadow-sm' 
                        : 'border-gray-100 hover:border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={16} className={syncMode === 'replace' ? 'text-danger' : 'text-gray-400'} />
                      <span className="text-[11px] font-bold uppercase tracking-wider text-brandDark">Wipe & Replace</span>
                    </div>
                    <p className="text-[10px] text-gray-400 leading-relaxed font-semibold">
                      Clears current Firestore inventory collection entirely and populates fresh from this Excel sheet.
                    </p>
                  </div>

                </div>
              </div>

              {/* Upload Dropzone Container */}
              <div className="space-y-4">
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400">
                  Upload Excel File
                </label>
                <div className="relative">
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-primary rounded-3xl cursor-pointer hover:bg-primary/[0.01] transition-all p-10 group bg-[#FAFAFA]">
                    <UploadCloud size={40} className="text-gray-300 group-hover:text-primary transition-colors mb-3 animate-bounce" />
                    <span className="text-xs text-brandDark font-bold uppercase tracking-widest text-center px-4">
                      {excelFileName ? excelFileName : 'Select Spreadsheet file'}
                    </span>
                    <span className="text-[9px] text-gray-400 uppercase tracking-widest mt-1.5 font-bold">Supports .xlsx, .xls, .csv</span>
                    <input 
                      type="file" 
                      accept=".xlsx, .xls, .csv" 
                      onChange={handleExcelUpload} 
                      className="hidden" 
                    />
                  </label>
                </div>
              </div>

              {/* Loader during Excel Parse */}
              {isProcessingExcel && (
                <div className="flex items-center gap-3 text-primary mt-6 p-4 bg-primary/5 rounded-2xl border border-primary/10 animate-pulse justify-center">
                  <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-[10px] font-bold uppercase tracking-widest">Verifying columns & structure...</span>
                </div>
              )}

              {/* Validation errors rendering */}
              {excelImportErrors && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-2xl mt-6">
                  <h4 className="font-heading font-bold text-[13px] uppercase tracking-wider text-red-950 mb-2 flex items-center gap-2">
                    <AlertCircle size={16} className="text-danger" />
                    Invalid Document Structure
                  </h4>
                  <ul className="list-disc pl-5 text-[11px] text-red-700 space-y-1">
                    {excelImportErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Excel Preview Grid and Action button */}
              {excelImportCars && excelImportCars.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100 space-y-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-emerald-50/50 p-4 rounded-2xl border border-emerald-100/50">
                    <div>
                      <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-emerald-950 flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-emerald-700" />
                        Spreadsheet Verification Succeeded
                      </h4>
                      <p className="text-[9px] text-emerald-800/80 font-bold uppercase mt-0.5">
                        Parsed {excelImportCars.length} validated units. Confirm commit to cloud lot.
                      </p>
                    </div>
                    
                    <button 
                      type="button"
                      disabled={isSyncing}
                      onClick={runCloudSyncEngine}
                      className="bg-brandDark hover:bg-emerald-800 text-white px-6 py-3.5 rounded-xl font-bold uppercase text-[9px] tracking-[0.15em] transition-all shadow-md flex items-center gap-2"
                    >
                      {isSyncing ? (
                        <>
                          <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Syncing lot...
                        </>
                      ) : (
                        'Commit to Cloud Lot'
                      )}
                    </button>
                  </div>

                  {/* MINI INTERACTIVE PREVIEW */}
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-3">
                      Records parsed from sheet ({excelImportCars.length})
                    </span>
                    <div className="border border-gray-100 rounded-2xl overflow-hidden max-h-[220px] overflow-y-auto">
                      <table className="w-full text-left border-collapse text-[11px]">
                        <thead className="bg-[#FAFAFA] font-bold uppercase text-[9px] tracking-wider text-gray-400 sticky top-0 border-b border-gray-100">
                          <tr>
                            <th className="px-4 py-3">Vehicle</th>
                            <th className="px-4 py-3">VIN</th>
                            <th className="px-4 py-3">Price</th>
                            <th className="px-4 py-3">Mil.</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 font-semibold">
                          {excelImportCars.map((car, idx) => (
                            <tr key={idx} className="hover:bg-brandGray/10">
                              <td className="px-4 py-3 text-brandDark">{car.year} {car.make} {car.model}</td>
                              <td className="px-4 py-3 font-mono text-[10px] text-gray-500">{car.vin || 'N/A'}</td>
                              <td className="px-4 py-3 text-emerald-700">${car.price.toLocaleString()}</td>
                              <td className="px-4 py-3 text-gray-500">{car.mileage.toLocaleString()} mi</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                </div>
              )}

              {/* SUCCESS MESSAGE INSET */}
              {syncSuccess && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-2xl mt-6 animate-fade-in flex items-start gap-3">
                  <CheckCircle2 size={24} className="text-emerald-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-emerald-950 mb-1">
                      Synchronization Accomplished
                    </h4>
                    <p className="text-xs text-emerald-800/85 leading-relaxed">
                      Your website inventory catalog has been successfully synced with your local Excel sheet data and persisted onto your connected Firebase Firestore database (<span className="font-mono text-[11px] font-bold">dakar1</span>). The public lot matches this database instantly!
                    </p>
                  </div>
                </div>
              )}

              {/* Terminal Logs Logger */}
              {syncLogs.length > 0 && (
                <div className="mt-8">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-3 flex items-center gap-1.5">
                    <Terminal size={12} />
                    Sync Activity Log
                  </span>
                  <div className="bg-[#1e1e24] p-5 rounded-2xl font-mono text-[10px] text-[#A0AEC0] border border-gray-800 max-h-[160px] overflow-y-auto space-y-1 shadow-inner select-none">
                    {syncLogs.map((log, idx) => {
                      let color = 'text-gray-400';
                      if (log.startsWith('[SUCCESS]')) color = 'text-emerald-400';
                      else if (log.startsWith('[ERROR]') || log.startsWith('[CRITICAL')) color = 'text-red-400';
                      else if (log.startsWith('[INSERT]')) color = 'text-[#FFCA28]';
                      else if (log.startsWith('[UPDATE]')) color = 'text-[#00B0FF]';
                      return (
                        <div key={idx} className={color}>
                          {log}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>

          </div>

        </div>

      </div>
    </div>
  );
};
